import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, QrCode, Link2, Search, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Logo, Page } from "@/components/shared";

export default function Join() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(null);
  const [showBands, setShowBands] = useState(false);
  const [bands, setBands] = useState([]);

  const verify = async (c) => {
    const value = (c || code).trim().toUpperCase();
    if (!value) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/bands/verify/${value}`);
      setVerified(data);
      setCode(value);
      toast.success(`Found ${data.name}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setVerified(null);
    } finally { setLoading(false); }
  };

  const loadBands = async () => {
    setShowBands(true);
    const { data } = await api.get("/bands");
    setBands(data);
  };

  const proceed = () => {
    nav("/register", { state: { band: verified } });
  };

  return (
    <Page className="cs-shell grain px-6 pt-6 pb-12" >
      <button data-testid="join-back-button" onClick={() => nav("/")} className="cs-tap flex items-center gap-1 text-sm mb-6" style={{ color: "var(--cs-fg2)" }}>
        <ArrowLeft size={18} /> Back
      </button>

      <Logo size="sm" className="mb-8" />

      <h1 className="font-head font-extrabold text-3xl">Join your band</h1>
      <p className="mt-2 text-base" style={{ color: "var(--cs-fg2)" }}>
        Enter your Band Code, scan a QR at costume pickup, or find a participating band.
      </p>

      <div className="mt-8">
        <label className="text-xs font-bold tracking-wide" style={{ color: "var(--cs-fg2)" }}>BAND CODE</label>
        <div className="flex gap-2 mt-2">
          <input
            data-testid="join-band-code-input"
            value={code}
            onChange={(e) => { setCode(e.target.value.toUpperCase()); setVerified(null); }}
            placeholder="e.g. ISLAND2026"
            className="flex-1 rounded-xl px-4 font-mono tracking-wider text-lg outline-none"
            style={{ height: 56, background: "var(--cs-card)", border: "1px solid var(--cs-border)", color: "#fff" }}
          />
          <button data-testid="join-verify-button" onClick={() => verify()} disabled={loading}
            className="cs-tap magenta-grad rounded-xl px-5 font-bold text-white" style={{ minWidth: 60 }}>
            {loading ? "…" : <ArrowRight size={20} />}
          </button>
        </div>

        {verified && (
          <div className="cs-card rise mt-4 p-4 flex items-center justify-between" style={{ borderColor: "var(--cs-green)" }}>
            <div>
              <p className="font-head font-bold text-lg">{verified.name}</p>
              <p className="text-sm" style={{ color: "var(--cs-fg2)" }}>{verified.tagline}</p>
            </div>
            <span className="grid place-items-center rounded-full" style={{ width: 34, height: 34, background: "var(--cs-green)" }}>
              <Check size={20} className="text-white" />
            </span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mt-6">
        <button data-testid="join-scan-qr-button" onClick={() => verify("ISLAND2026")}
          className="cs-card cs-tap flex flex-col items-center gap-2 py-5">
          <QrCode size={24} style={{ color: "var(--cs-magenta)" }} />
          <span className="text-sm font-semibold">Scan QR Code</span>
          <span className="text-[10px]" style={{ color: "var(--cs-fg2)" }}>at costume pickup</span>
        </button>
        <button data-testid="join-invite-link-button" onClick={() => verify("ISLAND2026")}
          className="cs-card cs-tap flex flex-col items-center gap-2 py-5">
          <Link2 size={24} style={{ color: "var(--cs-magenta)" }} />
          <span className="text-sm font-semibold">Invitation Link</span>
          <span className="text-[10px]" style={{ color: "var(--cs-fg2)" }}>from your band</span>
        </button>
      </div>

      <button data-testid="join-search-bands-button" onClick={loadBands}
        className="cs-tap w-full mt-3 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold"
        style={{ height: 52, background: "var(--cs-card)", border: "1px solid var(--cs-border)" }}>
        <Search size={18} /> Search participating bands
      </button>

      {showBands && (
        <div className="mt-4 space-y-2 rise">
          {bands.map((b) => (
            <button key={b.id} data-testid={`join-band-option-${b.code}`} onClick={() => verify(b.code)}
              className="cs-card cs-tap w-full p-4 flex items-center justify-between text-left">
              <div>
                <p className="font-semibold">{b.name}</p>
                <p className="text-xs" style={{ color: "var(--cs-fg2)" }}>{b.participant_count} masqueraders · {b.code}</p>
              </div>
              <ArrowRight size={18} style={{ color: "var(--cs-fg2)" }} />
            </button>
          ))}
        </div>
      )}

      {verified && (
        <button data-testid="join-continue-button" onClick={proceed}
          className="cs-tap w-full mt-8 magenta-grad magenta-glow rounded-2xl text-white font-head font-bold text-lg flex items-center justify-center gap-2"
          style={{ height: 58 }}>
          CONTINUE <ArrowRight size={20} />
        </button>
      )}
    </Page>
  );
}
